import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { PageHeader, Panel, PageLoader, SectionTitle, Btn, TextInput, TextArea } from "@/lib/ui";
import { MediaPicker, MediaImg } from "@/components/MediaPicker";
import { Globe, Save, Building2, Share2, Search, Megaphone } from "lucide-react";

const FIELDS = [
  "site_title", "tagline", "description", "contact_email", "contact_phone", "whatsapp",
  "address", "business_hours", "footer_note", "seo_title", "seo_description", "og_image",
  "map_embed_url", "primary_cta_label", "primary_cta_url", "social_instagram",
  "social_facebook", "social_linkedin", "social_youtube", "social_twitter",
];

export default function WebsiteSettings() {
  const api = useApi();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["site-settings"], queryFn: () => api.get("/cms/settings") });
  const [form, setForm] = useState({});
  useEffect(() => { if (data) { const f = {}; FIELDS.forEach((k) => { f[k] = data[k] ?? ""; }); setForm(f); } }, [data]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: () => api.patch("/cms/settings", form),
    onSuccess: () => { toast.success("Website settings saved"); qc.invalidateQueries({ queryKey: ["site-settings"] }); },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading) return <PageLoader />;

  return (
    <div data-testid="website-settings-page">
      <PageHeader kicker="Content · Website CMS" title="Website Settings">
        <Btn onClick={() => save.mutate()} disabled={save.isPending} data-testid="site-settings-save"><Save className="h-4 w-4" /> {save.isPending ? "Saving…" : "Save changes"}</Btn>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel className="p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1"><Building2 className="h-4 w-4 text-primary" /><SectionTitle>Site identity</SectionTitle></div>
          <TextInput label="Site title" value={form.site_title || ""} onChange={(e) => set("site_title", e.target.value)} data-testid="ss-site-title" />
          <TextInput label="Tagline" value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} data-testid="ss-tagline" />
          <TextArea label="Description" rows={3} value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="ss-description" />
          <TextInput label="Footer note" value={form.footer_note || ""} onChange={(e) => set("footer_note", e.target.value)} data-testid="ss-footer" />
        </Panel>

        <Panel className="p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1"><Globe className="h-4 w-4 text-primary" /><SectionTitle>Contact</SectionTitle></div>
          <div className="grid grid-cols-2 gap-3">
            <TextInput label="Contact email" value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} data-testid="ss-email" />
            <TextInput label="Contact phone" value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} data-testid="ss-phone" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextInput label="WhatsApp" value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} data-testid="ss-whatsapp" />
            <TextInput label="Business hours" value={form.business_hours || ""} onChange={(e) => set("business_hours", e.target.value)} data-testid="ss-hours" />
          </div>
          <TextArea label="Address" rows={2} value={form.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="ss-address" />
          <TextInput label="Map embed URL" value={form.map_embed_url || ""} onChange={(e) => set("map_embed_url", e.target.value)} data-testid="ss-map" />
        </Panel>

        <Panel className="p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1"><Share2 className="h-4 w-4 text-primary" /><SectionTitle>Social links</SectionTitle></div>
          <TextInput label="Instagram" value={form.social_instagram || ""} onChange={(e) => set("social_instagram", e.target.value)} data-testid="ss-instagram" />
          <TextInput label="Facebook" value={form.social_facebook || ""} onChange={(e) => set("social_facebook", e.target.value)} data-testid="ss-facebook" />
          <TextInput label="LinkedIn" value={form.social_linkedin || ""} onChange={(e) => set("social_linkedin", e.target.value)} data-testid="ss-linkedin" />
          <div className="grid grid-cols-2 gap-3">
            <TextInput label="YouTube" value={form.social_youtube || ""} onChange={(e) => set("social_youtube", e.target.value)} data-testid="ss-youtube" />
            <TextInput label="X / Twitter" value={form.social_twitter || ""} onChange={(e) => set("social_twitter", e.target.value)} data-testid="ss-twitter" />
          </div>
        </Panel>

        <Panel className="p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1"><Search className="h-4 w-4 text-primary" /><SectionTitle>SEO & sharing</SectionTitle></div>
          <TextInput label="Default SEO title" value={form.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} data-testid="ss-seo-title" />
          <TextArea label="Default SEO description" rows={2} value={form.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} data-testid="ss-seo-desc" />
          <div>
            <span className="text-xs text-muted-foreground mb-1.5 block">Default share image (OG)</span>
            {form.og_image && <MediaImg src={form.og_image} alt="" className="mb-2 h-28 w-full rounded-md border border-border object-cover" testid="ss-og-preview" />}
            <div className="flex items-center gap-2">
              <input className="w-full h-9 rounded-md border border-border bg-surface px-3 text-sm" placeholder="Image URL or pick from Media Library"
                value={form.og_image || ""} onChange={(e) => set("og_image", e.target.value)} data-testid="ss-og-url" />
              <MediaPicker folder="Website" onPick={(url) => set("og_image", url)} />
            </div>
          </div>
        </Panel>

        <Panel className="p-5 space-y-4 lg:col-span-2">
          <div className="flex items-center gap-2 mb-1"><Megaphone className="h-4 w-4 text-primary" /><SectionTitle>Primary call-to-action</SectionTitle></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextInput label="CTA label" value={form.primary_cta_label || ""} onChange={(e) => set("primary_cta_label", e.target.value)} data-testid="ss-cta-label" />
            <TextInput label="CTA link" value={form.primary_cta_url || ""} onChange={(e) => set("primary_cta_url", e.target.value)} data-testid="ss-cta-url" />
          </div>
        </Panel>
      </div>

      <div className="flex justify-end mt-4">
        <Btn onClick={() => save.mutate()} disabled={save.isPending} data-testid="site-settings-save-bottom"><Save className="h-4 w-4" /> {save.isPending ? "Saving…" : "Save changes"}</Btn>
      </div>
    </div>
  );
}
